import { OPENID_PARAMS } from '../../types/openid_enum';

type Params = Record<string, string | boolean>;

type UseQueryParamsOptions<T extends Params> = {
    requiredParams?: Array<keyof T>;
};

export enum QueryParamsStatus {
    Success,
    Error,
}

export type StateQueryParam = {
    state: string;
};

type QueryParamsError = {
    error: string;
    queryParams: undefined;
    status: QueryParamsStatus.Error;
};

type QueryParamsSuccess<T, U> = {
    error: undefined;
    queryParams: T & U;
    status: QueryParamsStatus.Success;
};

type QueryParamsResult<T, U> = QueryParamsError | QueryParamsSuccess<T, U>;

/**
 * Get the state from the URL query parameters.
 */
const useQueryParams = <QueryParams extends Params, StateParams = Params>(
    options: UseQueryParamsOptions<QueryParams> = {},
): QueryParamsResult<QueryParams, StateParams> => {
    const urlSearchParams = new URLSearchParams(window.location.search);

    /**
     * Validate the required query parameters.
     */
    if (options.requiredParams) {
        options.requiredParams.forEach((param) => {
            const paramStr = param as string;
            if (!urlSearchParams.has(paramStr)) {
                return {
                    error: `Missing required query parameter: ${paramStr}`,
                    queryParams: undefined,
                    status: QueryParamsStatus.Error,
                };
            }
        });
    }

    // Handle the base64 encoded state parameter
    let stateParams: StateParams;
    if (urlSearchParams.has(OPENID_PARAMS.STATE)) {
        try {
            const encodedState = urlSearchParams.get(OPENID_PARAMS.STATE) as string;
            const stringState = atob(encodedState); // Buffer.from(encodedState, "base64").toString() apparently not supported by browsers yet
            stateParams = JSON.parse(stringState);
        } catch (err) {
            return {
                error: `Error parsing state: ${(err as Error).message}`,
                queryParams: undefined,
                status: QueryParamsStatus.Error,
            };
        }
    } else {
        stateParams = {} as StateParams; // Default to an empty object if no state is provided
    }

    const rawQueryParams: Record<string, string> = {};

    urlSearchParams.forEach((value, key) => {
        rawQueryParams[key] = value as string;
    });
    const queryParams = rawQueryParams as QueryParams;

    return {
        error: undefined,
        queryParams: { ...stateParams, ...queryParams },
        status: QueryParamsStatus.Success,
    };
};

export { useQueryParams };
